import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * Servicio para manejar el PIN parental por familia en Firestore
 */

export interface FamilyPINConfig {
  parentalPIN: string | null; // PIN encriptado en Base64
  requirePIN: boolean; // Si se requiere PIN para salir del modo familiar
  pinSetupDate: number | null; // Timestamp de cuando se configuró
}

/**
 * Obtiene la configuración del PIN de una familia
 */
export async function getFamilyPINConfig(familyId: string): Promise<FamilyPINConfig> {
  try {
    const familyRef = doc(db, 'families', familyId);
    const familySnap = await getDoc(familyRef);
    
    if (!familySnap.exists()) {
      return {
        parentalPIN: null,
        requirePIN: false,
        pinSetupDate: null
      };
    }

    const familyData = familySnap.data();
    
    return {
      parentalPIN: familyData?.parentalPIN || null,
      requirePIN: familyData?.requirePIN || false,
      pinSetupDate: familyData?.pinSetupDate || null
    };
  } catch (error) {
    console.error('Error al obtener configuración PIN de familia:', error);
    return {
      parentalPIN: null,
      requirePIN: false,
      pinSetupDate: null
    };
  }
}

/**
 * Configura o actualiza el PIN de una familia
 */
export async function setFamilyPIN(
  familyId: string,
  pin: string // PIN en texto plano (se encriptará aquí)
): Promise<void> {
  try {
    const familyRef = doc(db, 'families', familyId);
    
    // Encriptar PIN (Base64)
    const encodedPIN = btoa(pin);
    
    await updateDoc(familyRef, {
      parentalPIN: encodedPIN,
      pinSetupDate: Date.now(),
      updatedAt: new Date()
    });
    
    console.log('✅ PIN configurado correctamente para familia:', familyId);
  } catch (error) {
    console.error('Error al configurar PIN de familia:', error);
    throw error;
  }
}

/**
 * Elimina el PIN de una familia
 */
export async function removeFamilyPIN(familyId: string): Promise<void> {
  try {
    const familyRef = doc(db, 'families', familyId);
    
    await updateDoc(familyRef, {
      parentalPIN: null,
      requirePIN: false,
      pinSetupDate: null,
      updatedAt: new Date()
    });
    
    console.log('✅ PIN eliminado correctamente para familia:', familyId);
  } catch (error) {
    console.error('Error al eliminar PIN de familia:', error);
    throw error;
  }
}

/**
 * Actualiza el estado de requirePIN de una familia
 */
export async function setRequirePIN(
  familyId: string,
  requirePIN: boolean
): Promise<void> {
  try {
    const familyRef = doc(db, 'families', familyId);
    
    await updateDoc(familyRef, {
      requirePIN,
      updatedAt: new Date()
    });
    
    console.log(`✅ RequirePIN actualizado a ${requirePIN} para familia:`, familyId);
  } catch (error) {
    console.error('Error al actualizar requirePIN de familia:', error);
    throw error;
  }
}

/**
 * Verifica si un PIN es correcto para una familia
 */
export async function verifyFamilyPIN(
  familyId: string,
  pin: string
): Promise<boolean> {
  try {
    const config = await getFamilyPINConfig(familyId);
    
    if (!config.parentalPIN) {
      return false;
    }

    // Comparar PIN encriptado
    const encodedPIN = btoa(pin);
    return encodedPIN === config.parentalPIN;
  } catch (error) {
    console.error('Error al verificar PIN de familia:', error);
    return false;
  }
}

/**
 * Migra el PIN de localStorage a Firestore para una familia
 * (útil para migración one-time)
 */
export async function migratePINFromLocalStorage(familyId: string): Promise<boolean> {
  try {
    const localPIN = localStorage.getItem('parental_pin');
    const localSetupDate = localStorage.getItem('parental_pin_setup_date');
    const localRequirePIN = localStorage.getItem('family_mode_require_pin') === 'true';
    
    if (!localPIN) {
      return false; // No hay PIN en localStorage
    }

    const familyRef = doc(db, 'families', familyId);
    
    await updateDoc(familyRef, {
      parentalPIN: localPIN, // Ya está en Base64
      requirePIN: localRequirePIN,
      pinSetupDate: localSetupDate ? parseInt(localSetupDate) : Date.now(),
      updatedAt: new Date()
    });
    
    console.log('✅ PIN migrado de localStorage a Firestore para familia:', familyId);
    
    // Limpiar localStorage
    localStorage.removeItem('parental_pin');
    localStorage.removeItem('parental_pin_setup_date');
    localStorage.removeItem('family_mode_require_pin');
    
    return true;
  } catch (error) {
    console.error('Error al migrar PIN de localStorage:', error);
    return false;
  }
}
